import { Doc, Id } from "@cvx/_generated/dataModel";
import { Plus, Search, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

export type FilterMode = "campaigns" | "creators" | "tags";

type CampaignWithApp = Doc<"campaigns"> & {
  app: { _id: Id<"apps">; name: string } | null;
};

type CreatorWithName = {
  _id: Id<"creators">;
  name: string;
  userId: Id<"users">;
};

interface FiltersProps {
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  // Campaigns
  showAllCampaigns: boolean;
  onShowAllCampaignsChange: (show: boolean) => void;
  selectedCampaigns: CampaignWithApp[];
  availableCampaigns: CampaignWithApp[];
  onAddCampaign: (id: Id<"campaigns">) => void;
  onRemoveCampaign: (id: Id<"campaigns">) => void;
  isPopoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  // Creators
  showAllCreators: boolean;
  onShowAllCreatorsChange: (show: boolean) => void;
  selectedCreators: CreatorWithName[];
  availableCreators: CreatorWithName[];
  creatorSearchQuery: string;
  onCreatorSearchQueryChange: (query: string) => void;
  onAddCreator: (id: Id<"creators">) => void;
  onRemoveCreator: (id: Id<"creators">) => void;
  isCreatorsLoading: boolean;
  // Tags
  showAllTags: boolean;
  onShowAllTagsChange: (show: boolean) => void;
  selectedTags: Set<string>;
  availableTags: string[];
  onToggleTag: (tag: string) => void;
}

export function Filters({
  filterMode,
  onFilterModeChange,
  showAllCampaigns,
  onShowAllCampaignsChange,
  selectedCampaigns,
  availableCampaigns,
  onAddCampaign,
  onRemoveCampaign,
  isPopoverOpen,
  onPopoverOpenChange,
  showAllCreators,
  onShowAllCreatorsChange,
  selectedCreators,
  availableCreators,
  creatorSearchQuery,
  onCreatorSearchQueryChange,
  onAddCreator,
  onRemoveCreator,
  isCreatorsLoading,
  showAllTags,
  onShowAllTagsChange,
  selectedTags,
  availableTags,
  onToggleTag,
}: FiltersProps) {
  return (
    <Tabs
      value={filterMode}
      onValueChange={(value) => onFilterModeChange(value as FilterMode)}
      className="w-full"
    >
      <TabsList>
        <TabsTrigger value="campaigns">Filter by Campaigns</TabsTrigger>
        <TabsTrigger value="creators">Filter by Creators</TabsTrigger>
        <TabsTrigger value="tags">Filter by Tags</TabsTrigger>
      </TabsList>

      {/* Campaigns Filter */}
      <TabsContent value="campaigns" className="mt-4">
        <div className="w-full">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Checkbox
              id="all-campaigns"
              checked={showAllCampaigns}
              onCheckedChange={(checked) => {
                onShowAllCampaignsChange(checked === true);
              }}
            />
            <label
              htmlFor="all-campaigns"
              className="text-sm font-medium cursor-pointer"
            >
              All Campaigns
            </label>
          </div>
          {!showAllCampaigns && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-primary/60">
                Selected Campaigns:
              </span>
              {selectedCampaigns.map((campaign) => (
                <Badge
                  key={campaign._id}
                  variant="secondary"
                  className="flex items-center gap-1.5 px-3 py-1"
                >
                  <span>
                    {campaign.name || `Campaign ${campaign._id.slice(-6)}`}
                    {campaign.app && (
                      <span className="text-xs text-primary/60 ml-1.5">
                        ({campaign.app.name})
                      </span>
                    )}
                  </span>
                  <button
                    onClick={() => onRemoveCampaign(campaign._id)}
                    className="ml-1 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
                    aria-label={`Remove ${campaign.name || "campaign"}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Popover open={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 gap-1.5">
                    <Plus className="h-4 w-4" />
                    Add Campaign
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <div className="p-2">
                    <div className="text-sm font-medium mb-2 px-2">
                      Select Campaigns
                    </div>
                    {availableCampaigns.length === 0 ? (
                      <div className="px-2 py-4 text-sm text-primary/60 text-center">
                        All campaigns are already selected
                      </div>
                    ) : (
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-1">
                          {availableCampaigns.map((campaign) => (
                            <button
                              key={campaign._id}
                              onClick={() => onAddCampaign(campaign._id)}
                              className="w-full text-left px-3 py-2 rounded-md hover:bg-secondary transition-colors"
                            >
                              <div className="flex flex-col items-start">
                                <span className="font-medium text-sm">
                                  {campaign.name ||
                                    `Campaign ${campaign._id.slice(-6)}`}
                                </span>
                                {campaign.app && (
                                  <span className="text-xs text-primary/60">
                                    {campaign.app.name}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </TabsContent>

      {/* Creators Filter */}
      <TabsContent value="creators" className="mt-4">
        <div className="w-full">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Checkbox
              id="all-creators"
              checked={showAllCreators}
              onCheckedChange={(checked) => {
                onShowAllCreatorsChange(checked === true);
              }}
            />
            <label
              htmlFor="all-creators"
              className="text-sm font-medium cursor-pointer"
            >
              All Creators
            </label>
          </div>
          {!showAllCreators && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-primary/60">
                Selected Creators:
              </span>
              {selectedCreators.map((creator) => (
                <Badge
                  key={creator._id}
                  variant="secondary"
                  className="flex items-center gap-1.5 px-3 py-1"
                >
                  <span>{creator.name || "Unknown Creator"}</span>
                  <button
                    onClick={() => onRemoveCreator(creator._id)}
                    className="ml-1 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
                    aria-label={`Remove ${creator.name || "creator"}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Popover open={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 gap-1.5">
                    <Plus className="h-4 w-4" />
                    Add Creator
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <div className="p-2">
                    <div className="text-sm font-medium mb-2 px-2">
                      Search and Select Creators
                    </div>
                    <div className="px-2 mb-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-primary/40" />
                        <Input
                          placeholder="Search creators..."
                          value={creatorSearchQuery}
                          onChange={(e) =>
                            onCreatorSearchQueryChange(e.target.value)
                          }
                          className="pl-8"
                        />
                      </div>
                    </div>
                    {isCreatorsLoading ? (
                      <div className="px-2 py-4 text-sm text-primary/60 text-center">
                        Loading creators...
                      </div>
                    ) : availableCreators.length === 0 ? (
                      <div className="px-2 py-4 text-sm text-primary/60 text-center">
                        {creatorSearchQuery
                          ? "No creators found"
                          : "All creators are already selected"}
                      </div>
                    ) : (
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-1">
                          {availableCreators.map((creator) => (
                            <button
                              key={creator._id}
                              onClick={() => onAddCreator(creator._id)}
                              className="w-full text-left px-3 py-2 rounded-md hover:bg-secondary transition-colors"
                            >
                              <span className="font-medium text-sm">
                                {creator.name || "Unknown Creator"}
                              </span>
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </TabsContent>

      {/* Tags Filter */}
      <TabsContent value="tags" className="mt-4">
        <div className="w-full">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Checkbox
              id="all-tags"
              checked={showAllTags}
              onCheckedChange={(checked) => {
                onShowAllTagsChange(checked === true);
              }}
            />
            <label
              htmlFor="all-tags"
              className="text-sm font-medium cursor-pointer"
            >
              All Tags
            </label>
          </div>
          {!showAllTags && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-primary/60">
                Selected Tags:
              </span>
              {Array.from(selectedTags).map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="flex items-center gap-1.5 px-3 py-1"
                >
                  <span>{tag}</span>
                  <button
                    onClick={() => onToggleTag(tag)}
                    className="ml-1 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
                    aria-label={`Remove ${tag}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <div className="flex items-center gap-2 ml-2">
                {availableTags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center gap-2 px-3 py-1.5 border rounded-md hover:bg-secondary transition-colors"
                  >
                    <Checkbox
                      id={`tag-${tag}`}
                      checked={selectedTags.has(tag)}
                      onCheckedChange={() => onToggleTag(tag)}
                    />
                    <label
                      htmlFor={`tag-${tag}`}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {tag}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}
